'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Grid3X3,
  Users,
  ShieldCheck,
  GraduationCap,
  Calendar,
  CheckSquare,
  Upload,
  X,
  Camera,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Edit2,
  Save,
  MessageCircle,
  FileText,
  MessageSquare,
  MapPin,
  CalendarCheck,
  Plus,
  Trash2,
  Check,
  Sparkles,
  Wand2,
  RefreshCw,
  Search
} from "lucide-react";
import { ProgressSteps } from "@/components/ProgressSteps";
import { ImageCropper } from "@/components/ImageCropper";
import { LocationMapPicker } from "@/components/LocationMapPicker";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { CustomToolsManager } from '@/components/CustomToolsManager';

const spaceTools = [
  {
    id: 'matrix',
    name: 'Matrix',
    icon: Grid3X3,
    description:
      'Create and manage training competency matrices to track skills and certifications across your workforce.',
    defaultEnabled: true,
    essential: true
  },
  {
    id: 'team',
    name: 'Team',
    icon: Users,
    description:
      'Manage team members, roles, and responsibilities. Track individual progress and assignments.',
    defaultEnabled: true,
    essential: true
  },
  {
    id: 'qualifications',
    name: 'Qualifications',
    icon: GraduationCap,
    description:
      'Manage training requirements, qualifications, and certifications needed for your workforce.',
    defaultEnabled: true,
    essential: true
  },
  {
    id: 'team_compliance',
    name: 'Team Compliance',
    icon: ShieldCheck,
    description:
      'AI-powered team compliance tracking to monitor training requirements and ensure workforce adherence to standards.',
    defaultEnabled: true,
    essential: true
  },
  {
    id: 'site_compliance',
    name: 'Site Compliance',
    icon: ShieldCheck,
    description:
      'Track site-specific compliance requirements and ensure locations meet regulatory and safety standards.',
    defaultEnabled: true
  },
  {
    id: 'bookings',
    name: 'Bookings',
    icon: Calendar,
    description:
      'Schedule and manage training sessions, workshops, and resource bookings with availability tracking.',
    defaultEnabled: true,
    essential: true
  },
  {
    id: 'message_board',
    name: 'Posts',
    icon: MessageSquare,
    description:
      'Post announcements, updates, and discussions. Keep everyone informed with threaded conversations.',
    defaultEnabled: true
  },
  {
    id: 'tasks',
    name: 'Tasks',
    icon: CheckSquare,
    description:
      'Create and manage tasks for training completion, compliance requirements, and space deliverables.',
    defaultEnabled: true
  },
  {
    id: 'docs',
    name: 'Docs',
    icon: FileText,
    description:
      'Store, organize, and collaborate on documents, training materials, and important files.',
    defaultEnabled: true
  },
  {
    id: 'checkins',
    name: 'Check-Ins',
    icon: MapPin,
    description:
      'QR code-based check-ins for sites. Track attendance and presence at physical locations.',
    defaultEnabled: true
  },
  {
    id: 'calendar',
    name: 'Calendar',
    icon: Calendar,
    description:
      'Set important dates, deadlines, and milestones. Sync with external calendars and get reminders.',
    defaultEnabled: true
  },
  {
    id: 'chat',
    name: 'Chat',
    icon: MessageCircle,
    description:
      'Real-time team communication with direct messages, group chats, and file sharing capabilities.',
    defaultEnabled: true
  },
  // {
  //   id: 'attendance',
  //   name: 'Attendance',
  //   icon: CalendarCheck,
  //   description:
  //     'Track team attendance, manage leave requests, bank holidays, and shift patterns.',
  //   defaultEnabled: true
  // }
];

const steps = [
  { title: 'Space basics', description: 'Name and details' },
  { title: 'Choose your tools', description: 'Select features' },
  { title: 'Team', description: 'Who has access' }
];

const presetColors = [
  { name: 'White', value: '#FFFFFF' },
  { name: 'Yellow', value: '#FEF08A' },
  { name: 'Orange', value: '#FED7AA' },
  { name: 'Pink', value: '#FECACA' },
  { name: 'Rose', value: '#F9A8D4' },
  { name: 'Purple', value: '#DDD6FE' },
  { name: 'Blue', value: '#BFDBFE' },
  { name: 'Green', value: '#BBF7D0' },
  { name: 'Gray', value: '#E5E7EB' },
  { name: 'Slate', value: '#CBD5E1' }
];

const imageStyles = [
  {
    id: 'vector',
    name: 'Flat Vector',
    description: 'Clean, modern flat design with solid colors',
    prompt: 'flat vector illustration style, minimalist, clean lines, solid colors'
  },
  {
    id: 'gradient',
    name: 'Gradient Modern',
    description: 'Modern design with smooth color gradients',
    prompt: 'modern gradient style, smooth color transitions, contemporary look'
  },
  {
    id: '3d',
    name: '3D Render',
    description: 'Three-dimensional rendered objects with depth',
    prompt: '3D rendered style, depth and shadows, polished surfaces'
  },
  {
    id: 'cartoon',
    name: 'Cartoon',
    description: 'Playful cartoon illustration style',
    prompt: 'cartoon illustration style, playful, fun, bold outlines'
  },
  {
    id: 'realistic',
    name: 'Photorealistic',
    description: 'Realistic photography style',
    prompt: 'photorealistic style, detailed, natural lighting, high quality'
  },
  {
    id: 'line-art',
    name: 'Line Art',
    description: 'Simple line drawings with minimal color',
    prompt: 'line art style, clean outlines, minimal detail, elegant simplicity'
  }
];

export function ProjectSettingsModal({ isOpen, onClose, project, onUpdateProject, onSaveSuccess, initialMode = 'details' }) {
  const { company, userProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentMembers, setCurrentMembers] = useState([]);
  const [companyManagers, setCompanyManagers] = useState([]);
  const [assignedManagers, setAssignedManagers] = useState([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentView, setCurrentView] = useState('settings'); // 'settings' or 'ai-generator'

  // Initialize form data with current project data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    locationData: null,
    industry: '',
    avatar: '',
    tools: spaceTools.reduce((acc, tool) => {
      acc[tool.id] = tool.defaultEnabled;
      return acc;
    }, {}),
    teamMembers: [],
    visibilityType: 'invite_only',
    customFields: {}
  });

  const [previewImage, setPreviewImage] = useState('');
  const [tempImageSrc, setTempImageSrc] = useState('');
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [isAddingCustomField, setIsAddingCustomField] = useState(false);
  const [newCustomFieldName, setNewCustomFieldName] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiColors, setAiColors] = useState([]);
  const [aiStyle, setAiStyle] = useState('vector');
  const [includeSpaceName, setIncludeSpaceName] = useState(true);
  const [includeSpaceDescription, setIncludeSpaceDescription] = useState(true);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const fileInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Generate UUID helper
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Reset view to settings when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentView('settings');
    }
  }, [isOpen]);

  // Initialize form data when project changes
  useEffect(() => {
    if (project && isOpen) {
      console.log('📂 Loading project data:', {
        name: project.name,
        location: project.location,
        location_data: project.location_data,
        industry: project.industry
      });
      console.log('🗺️ Raw location_data from project:', project.location_data);
      console.log('🗺️ location_data type:', typeof project.location_data);

      // Handle both JSON string and object for custom_fields (JSONB from database)
      let customFields = {};
      if (project.custom_fields) {
        if (typeof project.custom_fields === 'string') {
          try {
            customFields = JSON.parse(project.custom_fields);
          } catch (e) {
            console.error('Error parsing custom_fields JSON:', e);
          }
        } else if (typeof project.custom_fields === 'object') {
          customFields = project.custom_fields;
        }
      }

      // Handle both JSON string and object for tools (JSONB from database)
      let tools = spaceTools.reduce((acc, tool) => {
        acc[tool.id] = tool.defaultEnabled;
        return acc;
      }, {});

      if (project.tools) {
        if (typeof project.tools === 'string') {
          try {
            tools = JSON.parse(project.tools);
          } catch (e) {
            console.error('Error parsing tools JSON:', e);
          }
        } else if (typeof project.tools === 'object') {
          tools = project.tools;
        }
      }

      // Handle both JSON string and object for location_data (JSONB from database)
      let locationData = null;
      if (project.location_data) {
        console.log('🔍 Found location_data, parsing...');
        if (typeof project.location_data === 'string') {
          try {
            locationData = JSON.parse(project.location_data);
            console.log('✅ Parsed location_data from string:', locationData);
          } catch (e) {
            console.error('❌ Error parsing location_data JSON:', e);
          }
        } else if (typeof project.location_data === 'object') {
          locationData = project.location_data;
          console.log('✅ Using location_data as object:', locationData);
        }
      } else {
        console.log('⚠️ No location_data found in project object');
      }

      setFormData({
        name: project.name || '',
        description: project.description || '',
        location: project.location || '',
        locationData: locationData,
        industry: project.industry || '',
        avatar: project.avatar || '',
        tools: tools,
        teamMembers: [],
        visibilityType: 'invite_only', // All spaces are invite_only now
        customFields: customFields
      });
      setPreviewImage(project.avatar || '');

      // Fetch fresh access_control from database to ensure we have latest data
      const fetchAccessControl = async () => {
        const { data, error } = await supabase
          .from('projects')
          .select('access_control')
          .eq('id', project.id)
          .single();

        if (!error && data) {
          console.log('📋 Fetched access_control:', data.access_control);
          setAssignedManagers(data.access_control || []);
        } else {
          console.log('📋 Using project.access_control:', project.access_control);
          setAssignedManagers(project.access_control || []);
        }
      };
      fetchAccessControl();

      // Set initial step based on mode
      const stepMap = {
        details: 1,
        tools: 2,
        access: 3
      };
      setCurrentStep(stepMap[initialMode] || 1);

      fetchCurrentMembers();
      fetchCompanyManagers();
    }
  }, [project, isOpen, initialMode]);

  // Fetch team members from the same company
  useEffect(() => {
    if (isOpen && company?.id) {
      fetchTeamMembers();
    }
  }, [isOpen, company?.id]);

  const fetchCurrentMembers = async () => {
    if (!project?.id) return;

    try {
      const { data, error } = await supabase
        .from('space_members')
        .select(
          `
          user_id,
          role,
          user_profiles!user_id (
            id,
            email,
            first_name,
            last_name,
            avatar_url
          )
        `
        )
        .eq('project_id', project.id);

      if (error) throw error;

      const formattedMembers =
        data?.map(member => ({
          id: member.user_profiles.id,
          name:
            `${member.user_profiles.first_name || ''} ${
              member.user_profiles.last_name || ''
            }`.trim() || member.user_profiles.email,
          email: member.user_profiles.email,
          role: member.role === 'Owner' ? '(Owner)' : '',
          avatar: member.user_profiles.avatar_url || null,
          initials: `${
            (member.user_profiles.first_name || member.user_profiles.email)[0]
          }${(member.user_profiles.last_name || '')[0] || ''}`.toUpperCase(),
          isOwner: member.role === 'Owner'
        })) || [];

      setCurrentMembers(formattedMembers);
      // Initialize form data with current members (excluding owner)
      // All spaces are invite_only now
      setFormData(prev => ({
        ...prev,
        teamMembers: formattedMembers.filter(m => !m.isOwner)
      }));
    } catch (error) {
      console.error('Error fetching current members:', error);
    }
  };

  const fetchTeamMembers = async () => {
    if (!company?.id) return;

    setIsLoadingTeam(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, first_name, last_name, avatar_url, company_id')
        .eq('company_id', company.id);

      if (error) throw error;

      const formattedMembers =
        data?.map(member => ({
          id: member.id,
          name:
            `${member.first_name || ''} ${member.last_name || ''}`.trim() ||
            member.email,
          email: member.email,
          role: member.id === userProfile?.id ? '(You)' : '',
          avatar: member.avatar_url || null,
          initials: `${(member.first_name || member.email)[0]}${
            (member.last_name || '')[0] || ''
          }`.toUpperCase(),
          isCurrentUser: member.id === userProfile?.id
        })) || [];

      setTeamMembers(formattedMembers);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoadingTeam(false);
    }
  };

  // Fetch company managers for access control
  const fetchCompanyManagers = async () => {
    if (!company?.id) return;

    setIsLoadingManagers(true);
    try {
      const { data, error } = await supabase
        .from('company_members')
        .select(`
          user_id,
          role,
          user_profiles!user_id (
            id,
            email,
            first_name,
            last_name,
            avatar_url
          )
        `)
        .eq('company_id', company.id)
        .eq('role', 'Manager');

      if (error) throw error;

      const formattedManagers = data?.map(member => ({
        id: member.user_profiles.id,
        name: `${member.user_profiles.first_name || ''} ${member.user_profiles.last_name || ''}`.trim() || member.user_profiles.email,
        email: member.user_profiles.email,
        avatar: member.user_profiles.avatar_url || null,
        initials: `${(member.user_profiles.first_name || member.user_profiles.email)[0]}${(member.user_profiles.last_name || '')[0] || ''}`.toUpperCase()
      })) || [];

      setCompanyManagers(formattedManagers);
    } catch (error) {
      console.error('Error fetching company managers:', error);
    } finally {
      setIsLoadingManagers(false);
    }
  };

  // Toggle manager assignment for access control
  const handleManagerToggle = (managerId) => {
    setAssignedManagers(prev =>
      prev.includes(managerId)
        ? prev.filter(id => id !== managerId)
        : [...prev, managerId]
    );
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToolToggle = toolId => {
    setFormData(prev => ({
      ...prev,
      tools: {
        ...prev.tools,
        [toolId]: !prev.tools[toolId]
      }
    }));
  };

  // Handle custom tools changes
  const handleCustomToolsChange = customTools => {
    setFormData(prev => ({
      ...prev,
      tools: {
        ...prev.tools,
        _custom: customTools
      }
    }));
  };

  const handleTeamMemberToggle = member => {
    setFormData(prev => ({
      ...prev,
      teamMembers: prev.teamMembers.find(m => m.id === member.id)
        ? prev.teamMembers.filter(m => m.id !== member.id)
        : [...prev.teamMembers, member]
    }));
  };

  // Handle location data changes from LocationMapPicker
  const handleLocationChange = useCallback(data => {
    setFormData(prev => ({
      ...prev,
      locationData: data,
      // Also update the text location field for backwards compatibility
      location: data?.mainAddress || prev.location
    }));
  }, []);

  const handleCustomFieldChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [key]: value
      }
    }));
  };

  const handleDeleteCustomField = key => {
    setFormData(prev => {
      const newCustomFields = { ...prev.customFields };
      delete newCustomFields[key];
      return {
        ...prev,
        customFields: newCustomFields
      };
    });
  };

  const handleAddCustomField = () => {
    setIsAddingCustomField(true);
  };

  const handleSaveNewCustomField = () => {
    if (newCustomFieldName.trim()) {
      setFormData(prev => ({
        ...prev,
        customFields: {
          ...prev.customFields,
          [newCustomFieldName.trim()]: ''
        }
      }));
      setNewCustomFieldName('');
      setIsAddingCustomField(false);
    }
  };

  const handleCancelAddCustomField = () => {
    setNewCustomFieldName('');
    setIsAddingCustomField(false);
  };

  const handleImageUpload = event => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = e => {
        setTempImageSrc(e.target.result);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const handleCropComplete = croppedImage => {
    setPreviewImage(croppedImage);
    setFormData(prev => ({
      ...prev,
      avatar: croppedImage
    }));
    setIsCropperOpen(false);
    setTempImageSrc('');
  };

  const handleColorToggle = (colorValue) => {
    setAiColors(prev =>
      prev.includes(colorValue)
        ? prev.filter(c => c !== colorValue)
        : [...prev, colorValue]
    );
  };

  const handleGenerateAIAvatar = async () => {
    if (!aiPrompt.trim()) {
      alert('Please enter a prompt for the AI image generator');
      return;
    }

    setIsGeneratingAI(true);
    try {
      const uuid = project?.id || generateUUID();
      const selectedStyle = imageStyles.find(s => s.id === aiStyle);

      // Build a natural, human-readable prompt for the AI
      let fullPrompt = 'Generate a professional 1:1 square avatar image (1024x1024px) suitable for a workspace/project logo. ';
      fullPrompt += 'The image should work well at small sizes (like thumbnails) and look good in a circular crop. ';
      fullPrompt += `\n\nImage description: ${aiPrompt.trim()}`;

      if (includeSpaceName && formData.name) {
        fullPrompt += `\n\nThis avatar is for a workspace called "${formData.name}"`;
        if (includeSpaceDescription && formData.description) {
          fullPrompt += ` - ${formData.description}`;
        }
        fullPrompt += '. Make sure the image represents this workspace appropriately.';
      } else if (includeSpaceDescription && formData.description) {
        fullPrompt += `. Context: ${formData.description}`;
      }

      if (aiColors.length > 0) {
        const colorNames = aiColors.map(colorValue => {
          const color = presetColors.find(c => c.value === colorValue);
          return color ? color.name : colorValue;
        });
        fullPrompt += `\n\nImportant: Prominently incorporate these colors in the design: ${colorNames.join(', ')}.`;
      }

      fullPrompt += `\n\nStyle: Create this in a ${selectedStyle.prompt}. `;
      fullPrompt += 'IMPORTANT: Do not include any text, letters, numbers, or words in the image whatsoever. Ensure good contrast and visibility. Keep it professional and suitable for business use.';

      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 8);
      const imageName = `avatar-${timestamp}-${randomStr}.png`;

      console.log('🎨 Generating AI avatar with prompt:', fullPrompt);

      const response = await fetch('https://n8n.add.ai/webhook/image-generator-SM', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: fullPrompt,
          company_id: company?.id,
          project_id: uuid,
          image_name: imageName
        })
      });

      const data = await response.json();

      if (data && data.publicUrl) {
        console.log('✅ Generated AI avatar:', data.publicUrl);
        setGeneratedImageUrl(data.publicUrl);
      } else {
        console.error('❌ Unexpected response format:', data);
        alert('Image generated but response format was unexpected.');
      }
    } catch (error) {
      console.error('Error generating AI avatar:', error);
      alert('Failed to generate AI avatar. Please try again.');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleSaveGeneratedImage = () => {
    if (generatedImageUrl) {
      setPreviewImage(generatedImageUrl);
      setFormData(prev => ({
        ...prev,
        avatar: generatedImageUrl
      }));
      setCurrentView('settings');
      setGeneratedImageUrl(null);
    }
  };

  const handleCloseAiGenerator = () => {
    setCurrentView('settings');
    setGeneratedImageUrl(null);
    setIsGeneratingAI(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      console.log('💾 Saving project with data:', {
        location: formData.location,
        industry: formData.industry,
        project_id: project.id,
        access_control: assignedManagers
      });

      // Update project details including access_control
      const { data: updatedData, error: projectError } = await supabase
        .from('projects')
        .update({
          name: formData.name,
          description: formData.description,
          location: formData.location,
          location_data: formData.locationData,
          industry: formData.industry,
          avatar: formData.avatar,
          tools: formData.tools,
          visibility_type: formData.visibilityType,
          custom_fields: formData.customFields,
          access_control: assignedManagers,
          updated_date: new Date().toISOString()
        })
        .eq('id', project.id)
        .select();

      if (projectError) {
        console.error('❌ Project save error:', projectError);
        throw projectError;
      }

      console.log('✅ Project saved successfully:', updatedData);

      // Update team members (all spaces are invite_only)
      // Get current members (excluding owner)
      const currentNonOwnerMembers = currentMembers.filter(m => !m.isOwner);
      const selectedMembers = formData.teamMembers;

      // Find members to add and remove
      const membersToAdd = selectedMembers.filter(
        selected =>
          !currentNonOwnerMembers.find(current => current.id === selected.id)
      );

      const membersToRemove = currentNonOwnerMembers.filter(
        current =>
          !selectedMembers.find(selected => selected.id === current.id)
      );

      // Remove members
      for (const member of membersToRemove) {
        await supabase
          .from('space_members')
          .delete()
          .eq('project_id', project.id)
          .eq('user_id', member.id);
      }

      // Add new members
      for (const member of membersToAdd) {
        await supabase.from('space_members').insert({
          project_id: project.id,
          user_id: member.id,
          role: 'Member',
          created_by: userProfile?.id
        });
      }

      // Call the success callback to trigger a refetch in the parent
      if (onSaveSuccess) {
        onSaveSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update space. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Navigation functions removed - no longer needed for single section editing

  const ToolCard = ({ tool, isEnabled }) => {
    const IconComponent = tool.icon;
    const isEssential = tool.essential;

    return (
      <div
        className={`border rounded-lg p-4 transition-all ${
          isEssential
            ? 'bg-gray-50 border-gray-300 cursor-not-allowed opacity-75'
            : isEnabled
            ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/20 dark:border-blue-700 cursor-pointer'
            : 'bg-white hover:bg-gray-50 border-gray-200 cursor-pointer'
        }`}
        onClick={() => !isEssential && handleToolToggle(tool.id)}
      >
        <div className="flex items-start space-x-3">
          <div className={`flex items-center justify-center w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 ${
            isEnabled
              ? 'bg-blue-600 border-blue-600'
              : 'border-gray-300'
          }`}>
            {isEnabled && (
              <Check className="h-3 w-3 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <IconComponent className={`h-4 w-4 ${isEnabled ? 'text-blue-600' : 'text-gray-500'}`} />
              <p className={`font-medium ${isEnabled ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900'}`}>
                {tool.name}
                {isEssential && <span className="ml-2 text-xs text-gray-500">(Essential)</span>}
              </p>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{tool.description}</p>
          </div>
        </div>
      </div>
    );
  };

  if (!project) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`w-full bg-white p-0 gap-0 overflow-hidden ${
        currentView === 'ai-generator' ? '!max-w-6xl h-[85vh]' : '!max-w-3xl max-h-[90vh]'
      }`}>
        {currentView === 'settings' ? (
          // Settings View
          <div className="overflow-y-auto max-h-[90vh]">
            <div className="p-6">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-center">
                  {currentStep === 1 && 'Edit Details'}
                  {currentStep === 2 && 'Edit Tools'}
                  {currentStep === 3 && 'Edit Access'}
                </DialogTitle>
              </DialogHeader>

            {/* Progress Steps - Only show when in full mode (not from dropdown) */}
            {initialMode === 'details' && currentStep === 1 && null}

            <div className="space-y-6 mt-6">
          {/* Step 1: Space basics */}
          {currentStep === 1 && (
            <div className='space-y-6'>
              {/* Avatar Upload */}
              <div className='space-y-2'>
                <Label className='text-base'>Space Avatar</Label>
                <div className='flex items-center space-x-4'>
                  <Avatar className='h-16 w-16'>
                    <AvatarImage
                      src={previewImage}
                      alt='Space avatar'
                      className='object-cover'
                    />
                    <AvatarFallback>
                      <Camera className='h-6 w-6' />
                    </AvatarFallback>
                  </Avatar>
                  <div className='space-y-2'>
                    {!previewImage ? (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex items-center space-x-2"
                          disabled={isGeneratingAI}
                        >
                          <Upload className="h-4 w-4" />
                          <span>Upload Image</span>
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setCurrentView('ai-generator')}
                          disabled={!formData.name.trim()}
                          className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                        >
                          <Sparkles className="h-4 w-4" />
                          <span>Generate with AI</span>
                        </Button>
                      </div>
                    ) : (
                      <div className='flex gap-2'>
                        <Button
                          type='button'
                          variant='outline'
                          onClick={() => {
                            setTempImageSrc(previewImage);
                            setIsCropperOpen(true);
                          }}
                          className="flex items-center space-x-2"
                          disabled={isGeneratingAI}
                        >
                          <Edit2 className='h-4 w-4' />
                          <span>Edit</span>
                        </Button>
                        <Button
                          type='button'
                          variant='outline'
                          onClick={() => fileInputRef.current?.click()}
                          className="flex items-center space-x-2"
                          disabled={isGeneratingAI}
                        >
                          <Upload className='h-4 w-4' />
                          <span>Change</span>
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setCurrentView('ai-generator')}
                          disabled={!formData.name.trim()}
                          className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                        >
                          <Sparkles className="h-4 w-4" />
                          <span>Generate with AI</span>
                        </Button>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type='file'
                      accept='image/*'
                      onChange={handleImageUpload}
                      className='hidden'
                    />
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG up to 2MB{formData.name.trim() ? '' : ' · Enter a space name to use AI generation'}
                    </p>
                  </div>
                </div>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='space-name' className='text-base'>
                  Name this space
                </Label>
                <Input
                  id='space-name'
                  placeholder='e.g. Office Renovation'
                  value={formData.name}
                  onChange={e => handleInputChange('name', e.target.value)}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='space-description' className='text-base'>
                  Space Description
                </Label>
                <Textarea
                  id='space-description'
                  placeholder='e.g. Plans and scheduling for expanding the office'
                  value={formData.description}
                  onChange={e =>
                    handleInputChange('description', e.target.value)
                  }
                  className='min-h-24'
                />
              </div>

              <div className='space-y-2'>
                <Label className='text-base'>Location</Label>
                <LocationMapPicker
                  value={formData.locationData}
                  onChange={handleLocationChange}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='space-industry' className='text-base'>
                  Industry
                </Label>
                <Input
                  id='space-industry'
                  placeholder='e.g. Construction, Manufacturing, Healthcare'
                  value={formData.industry}
                  onChange={e => handleInputChange('industry', e.target.value)}
                />
              </div>

              {/* Custom Fields Section */}
              {(Object.keys(formData.customFields).length > 0 ||
                isAddingCustomField) && (
                <div className='space-y-4 pt-4 border-t'>
                  <div className='flex items-center justify-between'>
                    <Label className='text-base font-semibold'>
                      Custom Fields
                    </Label>
                    {!isAddingCustomField && (
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={handleAddCustomField}
                        className='h-8'
                      >
                        <Plus className='h-4 w-4 mr-1' />
                        Add Field
                      </Button>
                    )}
                  </div>
                  <div className='space-y-3'>
                    {Object.entries(formData.customFields).map(
                      ([key, value]) => (
                        <div key={key} className='flex items-start gap-2'>
                          <div className='flex-1 space-y-1'>
                            <Label
                              htmlFor={`custom-${key}`}
                              className='text-sm font-medium text-muted-foreground'
                            >
                              {key}
                            </Label>
                            <Input
                              id={`custom-${key}`}
                              value={value}
                              onChange={e =>
                                handleCustomFieldChange(key, e.target.value)
                              }
                              placeholder={`Enter ${key}...`}
                            />
                          </div>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => handleDeleteCustomField(key)}
                            className='mt-6 h-9 w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50'
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        </div>
                      )
                    )}

                    {/* Inline Add New Field */}
                    {isAddingCustomField && (
                      <div className='flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
                        <div className='flex-1 space-y-2'>
                          <Label
                            htmlFor='new-custom-field'
                            className='text-sm font-medium'
                          >
                            New Custom Field Name
                          </Label>
                          <Input
                            id='new-custom-field'
                            value={newCustomFieldName}
                            onChange={e =>
                              setNewCustomFieldName(e.target.value)
                            }
                            placeholder='Enter field name (e.g., Project Code, Budget, etc.)'
                            autoFocus
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveNewCustomField();
                              } else if (e.key === 'Escape') {
                                handleCancelAddCustomField();
                              }
                            }}
                          />
                        </div>
                        <div className='flex gap-1 mt-6'>
                          <Button
                            type='button'
                            size='sm'
                            onClick={handleSaveNewCustomField}
                            disabled={!newCustomFieldName.trim()}
                            className='h-9 bg-green-600 hover:bg-green-700'
                          >
                            <Save className='h-4 w-4' />
                          </Button>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={handleCancelAddCustomField}
                            className='h-9 w-9 p-0'
                          >
                            <X className='h-4 w-4' />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Add Custom Field Button - Show when no custom fields exist and not adding */}
              {Object.keys(formData.customFields).length === 0 &&
                !isAddingCustomField && (
                  <div className='pt-4 border-t'>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={handleAddCustomField}
                      className='w-full'
                    >
                      <Plus className='h-4 w-4 mr-2' />
                      Add Custom Field
                    </Button>
                  </div>
                )}
            </div>
          )}

          {/* Step 2: Choose your tools */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Choose your tools</h3>
                  <p className="text-sm text-muted-foreground">You can always change tools later.</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const nonEssentialTools = spaceTools.filter(tool => !tool.essential);
                    const allNonEssentialEnabled = nonEssentialTools.every(tool => formData.tools[tool.id]);
                    const newTools = { ...formData.tools };
                    spaceTools.forEach(tool => {
                      // Keep essential tools enabled, toggle non-essential ones
                      if (!tool.essential) {
                        newTools[tool.id] = !allNonEssentialEnabled;
                      }
                    });
                    handleInputChange('tools', newTools);
                  }}
                >
                  {spaceTools.filter(tool => !tool.essential).every(tool => formData.tools[tool.id]) ? 'Disable All' : 'Enable All'}
                </Button>
              </div>

              <div className='flex justify-center w-full'>
                <div className='flex flex-wrap justify-center gap-4 w-full max-w-4xl'>
                  {spaceTools.map(tool => (
                    <div key={tool.id} className='flex w-full sm:w-[calc(50%-8px)] max-w-[400px]'>
                      <ToolCard
                        tool={tool}
                        isEnabled={formData.tools[tool.id] || false}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Tools Section */}
              <div className="mt-8 pt-6 border-t">
                <CustomToolsManager
                  customTools={formData.tools._custom || []}
                  onChange={handleCustomToolsChange}
                />
              </div>
            </div>
          )}

          {/* Step 3: Manager Access Control */}
          {currentStep === 3 && (
            <div className='space-y-4'>
              <div>
                <h3 className='text-xl font-semibold'>
                  Manager Access Control
                </h3>
                <p className='text-sm text-muted-foreground'>
                  Select which managers can access and manage this space. Admins automatically have access to all spaces.
                </p>
              </div>

              {/* Search Input */}
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                <Input
                  type='text'
                  placeholder='Search managers...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='pl-10'
                />
              </div>

              {/* Managers Table */}
              <div className='border rounded-lg'>
                {isLoadingManagers ? (
                  <div className='flex items-center justify-center py-8'>
                    <Loader2 className='h-6 w-6 animate-spin' />
                  </div>
                ) : companyManagers.length === 0 ? (
                  <div className='text-center py-8'>
                    <Users className='h-8 w-8 text-muted-foreground mx-auto mb-2' />
                    <p className='text-sm text-muted-foreground'>
                      No managers in your company yet.
                    </p>
                    <p className='text-xs text-muted-foreground mt-1'>
                      Promote users to Manager role in Company Team settings.
                    </p>
                  </div>
                ) : (
                  <div className='max-h-[400px] overflow-y-auto'>
                    {companyManagers
                      .filter(manager => {
                        const query = searchQuery.toLowerCase();
                        return (
                          manager.name.toLowerCase().includes(query) ||
                          manager.email.toLowerCase().includes(query)
                        );
                      })
                      .sort((a, b) => {
                        // Selected managers first, then alphabetical
                        const aSelected = assignedManagers.includes(a.id);
                        const bSelected = assignedManagers.includes(b.id);
                        if (aSelected && !bSelected) return -1;
                        if (!aSelected && bSelected) return 1;
                        return a.name.localeCompare(b.name);
                      })
                      .map(manager => {
                        const isSelected = assignedManagers.includes(manager.id);

                        return (
                          <label
                            key={manager.id}
                            htmlFor={`manager-${manager.id}`}
                            className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 cursor-pointer ${
                              isSelected ? 'bg-green-50' : ''
                            }`}
                          >
                            <Checkbox
                              id={`manager-${manager.id}`}
                              checked={isSelected}
                              onCheckedChange={() => handleManagerToggle(manager.id)}
                            />
                            <Avatar className='h-7 w-7'>
                              <AvatarImage src={manager.avatar} alt={manager.name} />
                              <AvatarFallback className='text-xs'>{manager.initials}</AvatarFallback>
                            </Avatar>
                            <span className='font-medium text-sm min-w-[140px]'>{manager.name}</span>
                            <span className='text-sm text-muted-foreground truncate flex-1'>{manager.email}</span>
                          </label>
                        );
                      })}
                    {companyManagers.filter(manager => {
                      const query = searchQuery.toLowerCase();
                      return (
                        manager.name.toLowerCase().includes(query) ||
                        manager.email.toLowerCase().includes(query)
                      );
                    }).length === 0 && searchQuery && (
                      <div className='text-center py-8'>
                        <p className='text-sm text-muted-foreground'>
                          No managers found matching "{searchQuery}"
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className='flex justify-end gap-3 pt-4'>
            <Button variant='outline' onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>

            <Button
              onClick={handleSave}
              disabled={isSaving || (currentStep === 1 && !formData.name.trim())}
            >
              {isSaving ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
            </div>
          </div>
          </div>
        ) : (
          // AI Generator View
          <div className="grid grid-cols-2 h-full overflow-hidden">
            {/* Left Pane - Configuration */}
            <div className="flex flex-col h-full border-r bg-white overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-50 to-blue-50 flex-shrink-0">
                <div className="flex items-center gap-3 mb-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCloseAiGenerator}
                    disabled={isGeneratingAI}
                    className="h-8 w-8 p-0"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <DialogTitle className="text-lg font-bold">AI Avatar Generator</DialogTitle>
                </div>
                <p className="text-sm text-gray-600">Describe your ideal avatar and customize the style</p>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-6">
                  {/* Prompt Input */}
                  <div className="space-y-2">
                    <Label htmlFor="ai-prompt" className="text-base font-medium">
                      Describe Your Avatar
                    </Label>
                    <Textarea
                      id="ai-prompt"
                      placeholder="E.g., A construction site with cranes and workers wearing safety gear, modern industrial theme"
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      disabled={isGeneratingAI}
                      className="min-h-24 resize-none"
                    />
                    <p className="text-xs text-muted-foreground">
                      Be specific about objects, themes, or concepts you want to see
                    </p>
                  </div>

                  {/* Style Selection */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">Art Style</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {imageStyles.map((style) => (
                        <button
                          key={style.id}
                          type="button"
                          onClick={() => setAiStyle(style.id)}
                          disabled={isGeneratingAI}
                          className={`p-3 rounded-lg border-2 text-left transition-all hover:scale-[1.02] ${
                            aiStyle === style.id
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-sm mb-1">{style.name}</div>
                              <div className="text-xs text-muted-foreground">{style.description}</div>
                            </div>
                            {aiStyle === style.id && (
                              <Check className="h-5 w-5 text-purple-600 flex-shrink-0 ml-2" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Selection */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">Colors to Include (Optional)</Label>
                    <div className="grid grid-cols-5 gap-3">
                      {presetColors.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => handleColorToggle(color.value)}
                          disabled={isGeneratingAI}
                          className={`h-12 rounded-lg border-2 transition-all hover:scale-105 ${
                            color.value === '#FFFFFF' ? 'border-gray-300' : 'border-gray-200'
                          } ${
                            aiColors.includes(color.value)
                              ? 'ring-2 ring-purple-500 ring-offset-2 scale-105'
                              : ''
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        >
                          {aiColors.includes(color.value) && (
                            <div className="flex items-center justify-center">
                              <Check className="h-5 w-5 text-purple-600 font-bold drop-shadow-md" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    {aiColors.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {aiColors.map((colorValue) => {
                          const color = presetColors.find(c => c.value === colorValue);
                          return (
                            <Badge key={colorValue} variant="secondary" className="pl-2 pr-1 py-1">
                              <div
                                className="w-3 h-3 rounded-full mr-1.5 border"
                                style={{ backgroundColor: colorValue }}
                              />
                              <span>{color?.name || 'Custom'}</span>
                              <button
                                onClick={() => handleColorToggle(colorValue)}
                                className="ml-1 rounded-full hover:bg-gray-300 p-0.5"
                                disabled={isGeneratingAI}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Include Space Details Toggles */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">Include Space Details</Label>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="space-y-0.5">
                        <Label htmlFor="include-name" className="font-medium cursor-pointer text-sm">
                          Include Space Name
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {formData.name ? `Add "${formData.name}" to the prompt` : 'No name entered yet'}
                        </p>
                      </div>
                      <Switch
                        id="include-name"
                        checked={includeSpaceName}
                        onCheckedChange={setIncludeSpaceName}
                        disabled={isGeneratingAI}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="space-y-0.5">
                        <Label htmlFor="include-description" className="font-medium cursor-pointer text-sm">
                          Include Space Description
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {formData.description ? 'Add description for more context' : 'No description yet'}
                        </p>
                      </div>
                      <Switch
                        id="include-description"
                        checked={includeSpaceDescription}
                        onCheckedChange={setIncludeSpaceDescription}
                        disabled={isGeneratingAI}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer with action buttons */}
              <div className="px-6 py-4 border-t bg-gray-50 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseAiGenerator}
                    disabled={isGeneratingAI}
                  >
                    Cancel
                  </Button>

                  <div className="flex gap-2">
                    {generatedImageUrl && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleGenerateAIAvatar}
                          disabled={!aiPrompt.trim() || isGeneratingAI}
                          className="border-purple-300 text-purple-700 hover:bg-purple-50"
                        >
                          {isGeneratingAI ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="mr-2 h-4 w-4" />
                              Regenerate
                            </>
                          )}
                        </Button>
                        <Button
                          type="button"
                          onClick={handleSaveGeneratedImage}
                          disabled={isGeneratingAI}
                          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                        >
                          <Check className="mr-2 h-4 w-4" />
                          Save Image
                        </Button>
                      </>
                    )}

                    {!generatedImageUrl && (
                      <Button
                        type="button"
                        onClick={handleGenerateAIAvatar}
                        disabled={!aiPrompt.trim() || isGeneratingAI}
                        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                      >
                        {isGeneratingAI ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-4 w-4" />
                            Generate Image
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Pane - Image Preview */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-8">
              {isGeneratingAI ? (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-16 w-16 text-purple-600 animate-spin" />
                  <p className="text-lg font-medium text-gray-700">Generating your avatar...</p>
                  <p className="text-sm text-gray-500">This may take a few moments</p>
                </div>
              ) : generatedImageUrl ? (
                <div className="flex flex-col items-center gap-6 w-full max-w-lg">
                  <div className="relative w-full aspect-square rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                    <img
                      src={generatedImageUrl}
                      alt="Generated avatar"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold text-gray-800 mb-1">Avatar Generated!</p>
                    <p className="text-sm text-gray-600">Review your avatar and save it, or adjust settings and regenerate</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4 text-center max-w-md">
                  <div className="w-32 h-32 rounded-full bg-white/50 flex items-center justify-center">
                    <Wand2 className="h-16 w-16 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-lg font-medium text-gray-700 mb-2">Preview Your Avatar</p>
                    <p className="text-sm text-gray-500">Configure your settings on the left and click Generate to see your AI-created avatar here</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>

      {/* Image Cropper Modal */}
      <ImageCropper
        isOpen={isCropperOpen}
        onClose={() => {
          setIsCropperOpen(false);
          setTempImageSrc('');
        }}
        imageSrc={tempImageSrc}
        onCropComplete={handleCropComplete}
        aspectRatio={1}
        cropShape='round'
      />
    </Dialog>
  );
}
